/**
 * DisputesPage.tsx
 *
 * The chargebacks raised against the practice's card payments, and where each
 * one stands.
 *
 * ── Why the rows come from our own database ─────────────────────────────────
 * Forward's disputes API has no date filter, no search, and returns at most 50
 * per request. Reading it live means paging through history and discarding most
 * of it on every load. So disputes are archived as they are seen, and this page
 * reads that archive. A decided dispute can never change again, which is what
 * makes the copy trustworthy rather than merely fast.
 *
 * ── Why a dispute may have no patient ──────────────────────────────────────
 * A dispute names a card charge, not a person. The patient is matched through
 * the payment intent, and a charge taken outside this software matches nothing.
 * Those rows still matter — the amount and the deadline are real — so they show
 * a dash rather than being hidden or given a broken link.
 *
 * ── Responding ─────────────────────────────────────────────────────────────
 * Responses are submitted in Forward's own merchant portal. This page is a
 * read-only view of what is owed and what has been decided; the outcome appears
 * here once Forward records it.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, Download, ExternalLink, ShieldAlert } from 'lucide-react';
import { FilterSidebar } from '@/components/layout/FilterSidebar';
import { ListPageLayout } from '@/components/layout/ListPageLayout';
import { PageHeader } from '@/components/layout/PageHeader';
import { MetricCardGrid } from '@/components/ui/MetricCardGrid';
import { StatCard } from '@/components/ui/StatCard';
import { Pagination } from '@/components/ui/Pagination';
import { Button } from '@/components/ui/Button';
import { useAsyncExport } from '@/hooks/useAsyncExport';
import { FORWARD_PORTAL_DISPUTES_URL, forwardDisputeUrl } from '@/lib/forwardPortal';
import type { FilterConfig, FilterOption, FilterValue } from '@/types/filters.types';
import {
  getDisputes,
  getDispute,
  listMerchants,
  type DisputeRow,
} from '@/services/payments.service';

const PER_PAGE = 50;

/** Statuses that are still live. Mirrors ForwardDispute::OPEN_STATUSES. */
const OPEN_STATUSES = new Set(['new', 'contested', 'in_review', 'in_pre_arbitration']);

const FILTER_CONFIGS: FilterConfig[] = [
  { key: 'search', label: 'Search', type: 'search', allLabel: 'Search patient, card last 4 or ID…' },
  { key: 'status', label: 'Status', type: 'select', allLabel: 'Any status' },
  // Multi: an account can bank through several merchant accounts, and "what is
  // being disputed in total" is the union of them.
  { key: 'merchant', label: 'Merchant', type: 'select', allLabel: 'All merchants', multi: true },
  { key: 'daterange', label: 'Dispute Date', type: 'daterange' },
];

/**
 * Forward's statuses in the words a front-desk reader needs. `new` is the one
 * state that needs somebody to act today, and `contested` means the practice has
 * already answered — the raw tokens read backwards, so neither is shown.
 *
 * "Still open" is first because it is the question the page is usually opened to
 * answer. It is ours, not Forward's; the API expands it.
 */
const STATUS_OPTIONS: FilterOption[] = [
  { value: 'open', label: 'Still open' },
  { value: 'new', label: 'Needs response' },
  { value: 'contested', label: 'Response submitted' },
  { value: 'in_review', label: 'Under review' },
  { value: 'in_pre_arbitration', label: 'Pre-arbitration' },
  { value: 'won', label: 'Won' },
  { value: 'lost', label: 'Lost' },
  { value: 'accepted', label: 'Accepted' },
];

/**
 * Colour by outcome, not by name. A reader scanning the column needs to spot the
 * money at risk and the money already lost; everything mid-flight is neutral.
 */
const STATUS_TONES: Record<string, string> = {
  new: 'bg-rose-50 text-rose-700',
  won: 'bg-emerald-50 text-emerald-700',
  lost: 'bg-slate-100 text-slate-600',
  accepted: 'bg-slate-100 text-slate-600',
};

function money(value: number, currency: string): string {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currency || 'USD',
  }).format(value);
}

function shortDate(value: string | null): string {
  if (!value) return '—';
  const parsed = new Date(`${value.slice(0, 10)}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? '—' : parsed.toLocaleDateString();
}

/** Whole days from today until a deadline. Negative once it has passed. */
function daysUntil(value: string | null): number | null {
  if (!value) return null;
  const due = new Date(`${value.slice(0, 10)}T00:00:00`);
  if (Number.isNaN(due.getTime())) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((due.getTime() - today.getTime()) / 86_400_000);
}

/**
 * The deadline cell. Urgency is only meaningful while the dispute is open — a
 * won dispute's passed deadline is history, and colouring it red would send
 * staff chasing something already settled.
 */
function DueBy({ dispute }: { dispute: DisputeRow }) {
  const days = daysUntil(dispute.responseDueDate);
  const isOpen = OPEN_STATUSES.has(dispute.status);

  if (!dispute.responseDueDate || days === null || !isOpen) {
    return <span className="text-slate-500">{shortDate(dispute.responseDueDate)}</span>;
  }

  if (days < 0) {
    return (
      <span className="font-medium text-rose-600">
        {shortDate(dispute.responseDueDate)} · overdue
      </span>
    );
  }

  const tone = days <= 3 ? 'font-medium text-amber-700' : 'text-slate-600';
  return (
    <span className={tone}>
      {shortDate(dispute.responseDueDate)}
      {days <= 3 ? (days === 0 ? ' · today' : ` · ${days}d left`) : ''}
    </span>
  );
}

export default function DisputesPage() {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [merchants, setMerchants] = useState<string[]>([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const { exporting, startExport, exportModal } = useAsyncExport({
    entityType: 'disputes',
    entityLabel: 'disputes',
  });

  const { data: merchantList = [] } = useQuery({
    queryKey: ['payment-merchants'],
    queryFn: listMerchants,
    staleTime: 5 * 60_000,
  });

  const filters = useMemo(() => ({
    search: search || undefined,
    status: status || undefined,
    merchantIds: merchants.length ? merchants.map(Number) : undefined,
    dateStart: dateFrom || undefined,
    dateEnd: dateTo || undefined,
  }), [search, status, merchants, dateFrom, dateTo]);

  const filterKey = JSON.stringify(filters);
  useEffect(() => { setPage(1); }, [filterKey]);

  // Collapse on any filter or page change: the expanded set is keyed by dispute
  // id, and holding ids that are no longer on screen would re-open unrelated
  // rows the moment a filter brought them back.
  useEffect(() => { setExpanded(new Set()); }, [filterKey, page]);

  const { data, isFetching } = useQuery({
    queryKey: ['disputes', filterKey, page],
    queryFn: () => getDisputes({ ...filters, page, perPage: PER_PAGE }),
    // Rows stay on screen while the next page loads, so paging does not flash
    // an empty table.
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });

  const rows = data?.data ?? [];
  const total = data?.meta.total ?? 0;
  const totals = data?.meta.totals;

  const merchantOptions = useMemo<FilterOption[]>(
    () => merchantList.map((m) => ({ value: String(m.merchantId), label: m.friendlyTitle || 'Default' })),
    [merchantList],
  );

  const sidebarOptions = useMemo<Record<string, FilterOption[]>>(
    () => ({ status: STATUS_OPTIONS, merchant: merchantOptions }),
    [merchantOptions],
  );

  const sidebarValues = useMemo<Record<string, FilterValue>>(() => ({
    search: search ? [search] : [],
    status: status ? [status] : [],
    merchant: merchants,
    daterange: { from: dateFrom, to: dateTo },
  }), [search, status, merchants, dateFrom, dateTo]);

  const handleSidebarChange = useCallback((key: string, value: FilterValue) => {
    switch (key) {
      case 'search': if (Array.isArray(value)) setSearch(value[0] ?? ''); break;
      case 'status': if (Array.isArray(value)) setStatus(value[0] ?? ''); break;
      case 'merchant': if (Array.isArray(value)) setMerchants(value as string[]); break;
      case 'daterange':
        if (!Array.isArray(value) && 'from' in value) { setDateFrom(value.from || ''); setDateTo(value.to || ''); }
        break;
    }
  }, []);

  const handleSidebarClear = useCallback((key: string) => {
    switch (key) {
      case 'search': setSearch(''); break;
      case 'status': setStatus(''); break;
      case 'merchant': setMerchants([]); break;
      case 'daterange': setDateFrom(''); setDateTo(''); break;
    }
  }, []);

  const toggleRow = useCallback((disputeId: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(disputeId)) next.delete(disputeId);
      else next.add(disputeId);
      return next;
    });
  }, []);

  const hasActiveFilters = !!search || !!status || merchants.length > 0 || !!dateFrom || !!dateTo;

  return (
    <ListPageLayout
      filterSidebar={(
        <FilterSidebar
          filters={FILTER_CONFIGS}
          values={sidebarValues}
          options={sidebarOptions}
          onChange={handleSidebarChange}
          onClear={handleSidebarClear}
          searchCommitMode="change"
          showAIAssistant={false}
          storageKey="disputes"
        />
      )}
    >
      <div className="flex h-full min-h-0 flex-col max-md:h-auto">
        <div className="shrink-0 space-y-6 p-6">
          <PageHeader
            variant="compact"
            title="Disputes"
            actions={(
              <div className="flex gap-2">
                {/* Answering a chargeback only happens in Forward's portal, so
                    this is the way out of a read-only list. First in the row,
                    ahead of Export. */}
                <a
                  href={FORWARD_PORTAL_DISPUTES_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex h-9 shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-slate-300 bg-white px-4 text-sm font-medium text-slate-700 transition-colors duration-150 hover:bg-slate-50 active:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 max-md:min-h-[44px]"
                >
                  <ExternalLink className="h-4 w-4" />
                  See Disputes in Forward
                </a>
                <Button
                  variant="secondary"
                  leftIcon={<Download className="h-4 w-4" />}
                  onClick={() => startExport(filters)}
                  disabled={total === 0 || exporting}
                  loading={exporting}
                >
                  Export
                </Button>
                {exportModal}
              </div>
            )}
          />

          <MetricCardGrid columns={4} data-testid="disputes-kpis">
            <StatCard label="Needs response" value={totals?.needsResponse ?? 0} tone="rose" />
            <StatCard label="Still open" value={totals?.open ?? 0} tone="amber" />
            <StatCard label="Amount disputed" value={money(totals?.amount ?? 0, 'USD')} tone="blue" />
            <StatCard label="Won / Lost" value={`${totals?.won ?? 0} / ${totals?.lost ?? 0}`} tone="slate" />
          </MetricCardGrid>
        </div>

        <div className="min-h-0 flex-1 overflow-auto px-6 pb-6 max-md:overflow-visible">
          {rows.length === 0 && !isFetching ? (
            <div className="py-12 text-center">
              <ShieldAlert className="mx-auto h-8 w-8 text-slate-300" />
              <p className="mt-3 text-sm text-slate-500">
                {hasActiveFilters ? 'No disputes match your filters.' : 'No disputes.'}
              </p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto rounded-lg border border-slate-200">
                <table className="w-full text-left text-sm">
                  <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                    <tr>
                      {/* Spacer keeps the chevron out of the Date column so the
                          header labels stay above the values they name. */}
                      <th className="w-10 px-3 py-2" />
                      <th className="px-3 py-2">Date</th>
                      <th className="px-3 py-2">Status</th>
                      <th className="px-3 py-2">Patient</th>
                      <th className="px-3 py-2">Merchant</th>
                      <th className="px-3 py-2">Reason</th>
                      <th className="px-3 py-2 text-right">Amount</th>
                      <th className="px-3 py-2">Respond by</th>
                    </tr>
                  </thead>
                  <tbody className={isFetching ? 'opacity-60 transition-opacity' : undefined}>
                    {rows.map((r) => (
                      <React.Fragment key={r.id}>
                        <tr className="border-t border-slate-100 hover:bg-slate-50">
                          <td className="px-3 py-2">
                            <button
                              type="button"
                              onClick={() => toggleRow(r.id)}
                              className="text-slate-400 hover:text-slate-600"
                              aria-expanded={expanded.has(r.id)}
                              aria-label={
                                expanded.has(r.id)
                                  ? `Hide details for dispute ${r.id}`
                                  : `Show details for dispute ${r.id}`
                              }
                            >
                              {expanded.has(r.id)
                                ? <ChevronDown className="h-4 w-4" />
                                : <ChevronRight className="h-4 w-4" />}
                            </button>
                          </td>
                          <td className="whitespace-nowrap px-3 py-2 text-slate-600">{shortDate(r.disputeDate)}</td>
                          <td className="px-3 py-2">
                            <span
                              className={`inline-block whitespace-nowrap rounded px-2 py-0.5 text-xs font-medium ${
                                STATUS_TONES[r.status] ?? 'bg-slate-100 text-slate-600'
                              }`}
                            >
                              {r.statusLabel}
                            </span>
                          </td>
                          <td className="px-3 py-2">
                            {r.patientId > 0 ? (
                              <Link
                                to={`/patients/${r.patientId}?section=payments`}
                                className="text-blue-600 hover:underline"
                              >
                                {r.patientName || `Patient ${r.patientId}`}
                              </Link>
                            ) : (
                              /* No record of this charge here, which is normal
                                 for a payment taken elsewhere. The card digits
                                 are what staff use to find it. */
                              <span className="text-slate-400">
                                {r.cardLastFour ? `Card ending ${r.cardLastFour}` : '—'}
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-slate-600">{r.merchantName}</td>
                          <td className="px-3 py-2 text-slate-600">{r.reason || '—'}</td>
                          <td className="px-3 py-2 text-right font-medium tabular-nums">
                            {money(r.amount, r.currency)}
                          </td>
                          <td className="whitespace-nowrap px-3 py-2"><DueBy dispute={r} /></td>
                        </tr>
                        {expanded.has(r.id) && (
                          <tr className="border-t border-slate-100 bg-slate-50/60">
                            <td colSpan={8} className="px-3 py-3">
                              <DisputeDetails dispute={r} />
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>

              {total > PER_PAGE && (
                <Pagination
                  className="mt-4"
                  total={total}
                  page={page}
                  pageSize={PER_PAGE}
                  onPageChange={setPage}
                />
              )}
            </>
          )}
        </div>
      </div>
    </ListPageLayout>
  );
}

/** One labelled value in the detail panel. Blank values are dropped by Field. */
function Field({ label, value }: { label: string; value: React.ReactNode }) {
  if (value === null || value === undefined || value === '' || value === '—') return null;
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-slate-400">{label}</dt>
      <dd className="mt-0.5 text-slate-700">{value}</dd>
    </div>
  );
}

/**
 * Everything behind one expanded dispute.
 *
 * Its own component so the query mounts with the panel: react-query then caches
 * per dispute, and collapsing and re-opening a row costs nothing.
 */
function DisputeDetails({ dispute }: { dispute: DisputeRow }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['dispute-detail', dispute.id],
    queryFn: () => getDispute(dispute.id),
    staleTime: 5 * 60_000,
  });

  if (isLoading) {
    return <p className="py-2 text-sm text-slate-500">Loading details…</p>;
  }

  if (isError || !data) {
    return (
      <p className="py-2 text-sm text-slate-500">
        These details could not be loaded. Try again in a moment, or contact support if it keeps happening.
      </p>
    );
  }

  return (
    <div className="space-y-4 rounded border border-slate-200 bg-white p-4">
      <dl className="grid gap-4 text-sm sm:grid-cols-2 lg:grid-cols-4">
        <Field label="Reason" value={data.reason} />
        <Field label="Reason code" value={data.reasonCode || null} />
        <Field label="Received" value={shortDate(data.receivedDate)} />
        <Field label="Respond by" value={shortDate(data.responseDueDate)} />
        <Field label="Closed" value={shortDate(data.closedDate)} />
        <Field label="Decided" value={shortDate(data.decidedDate)} />
        <Field label="Card" value={data.cardLastFour ? `Ending ${data.cardLastFour}` : null} />
        <Field label="Authorization code" value={data.authorizationCode} />
        <Field
          label="Original charge"
          value={
            data.transactionAmount
              ? `${money(data.transactionAmount, data.currency)}${data.transactionDate ? ` on ${shortDate(data.transactionDate)}` : ''}`
              : null
          }
        />
        <Field label="Reference" value={data.retrievalReference} />
        <Field
          label="Payment"
          value={
            data.paymentId > 0 && data.patientId > 0 ? (
              <Link
                to={`/patients/${data.patientId}?section=payments`}
                className="text-blue-600 hover:underline"
              >
                {data.patientName ? `${data.patientName} · #${data.paymentId}` : `#${data.paymentId}`}
              </Link>
            ) : null
          }
        />
      </dl>

      {data.response ? (
        <div>
          <p className="text-xs uppercase tracking-wide text-slate-400">Response submitted</p>
          <p className="mt-1 whitespace-pre-wrap text-sm text-slate-700">{data.response}</p>
        </div>
      ) : null}

      {data.notes.length > 0 && (
        <div>
          <p className="text-xs uppercase tracking-wide text-slate-400">Notes</p>
          <ul className="mt-1 space-y-1 text-sm text-slate-700">
            {data.notes.map((note) => (
              <li key={note.id}>
                <span className="text-slate-400">{shortDate(note.date)}</span> — {note.message}
              </li>
            ))}
          </ul>
        </div>
      )}

      {data.attachments.length > 0 && (
        <p className="text-sm text-slate-500">
          {data.attachments.length === 1
            ? '1 file attached to this dispute.'
            : `${data.attachments.length} files attached to this dispute.`}
        </p>
      )}

      {/* Where a response is actually filed, and where any attached files can
          be read. Keyed on Forward's own dispute id, which is what this row
          already carries. */}
      <a
        href={forwardDisputeUrl(dispute.id)}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
      >
        <ExternalLink className="h-3.5 w-3.5" />
        Open this dispute in Forward
      </a>
    </div>
  );
}
